import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Chip, 
  Button,
  Input,
  Divider,
  Avatar,
  IconButton,
  Textarea
} from '@mui/joy';
import FlatpickrDateTimeField from './FlatpickrDateTimeField';
import { Modal } from '@mui/joy';
import { supabase } from '../../supabaseClient';
import EnhancedButton from '../EnhancedButton';


const TaskDetailPanel = ({ task, open, onClose, onTaskUpdated, canManageTasks, users = [] }) => {
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (task) {
      // Only allow 1, 2, or 3 for priority
      let safePriority = Number(task.priority);
      if (![1, 2, 3].includes(safePriority)) safePriority = 2;
      setEditTask({
        ...task,
        priority: safePriority,
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date ? task.due_date.slice(0, 16) : '', // for datetime-local
        assigned_to: task.assigned_to || '',
      });
      setError('');
    }
  }, [task]);

  if (!task || !editTask) return null;

  // Priority configuration
  const getPriorityConfig = (priority) => {
    switch (Number(priority)) {
      case 1:
        return { color: 'danger', icon: 'priority_high', label: 'High Priority' };
      case 2:
        return { color: 'warning', icon: 'drag_handle', label: 'Medium Priority' };
      case 3:
        return { color: 'success', icon: 'low_priority', label: 'Low Priority' };
      default:
        return { color: 'neutral', icon: 'help', label: 'No Priority' };
    }
  };

  const priorityConfig = getPriorityConfig(editTask.priority);
  const isOverdue = editTask.due_date && new Date(editTask.due_date) < new Date();

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setAddingComment(true);
    try {
      const { error } = await supabase
        .from('task_comments')
        .insert([{
          task_id: task.id,
          user_id: (await supabase.auth.getUser()).data.user.id,
          comment_text: newComment
        }]);

      if (error) throw error;

      setNewComment('');
      onTaskUpdated(); // Refresh the task data
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment: ' + error.message);
    } finally {
      setAddingComment(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="task-detail-modal"
      aria-describedby="task-detail-modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box sx={{ p: 3, bgcolor: 'background.body', borderRadius: 8, boxShadow: 24, width: { xs: '100vw', sm: 500, md: 600 }, maxWidth: '100vw', maxHeight: '90vh', overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Input
              value={editTask.title}
              onChange={e => setEditTask({ ...editTask, title: e.target.value })}
              sx={{ mb: 1, fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}
              fullWidth
              placeholder="Task Title"
              disabled={saving || deleting}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <select
                value={editTask.priority}
                onChange={e => setEditTask({ ...editTask, priority: Number(e.target.value) })}
                disabled={saving || deleting}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 14 }}
              >
                <option value={1}>High</option>
                <option value={2}>Medium</option>
                <option value={3}>Low</option>
              </select>
              {isOverdue && (
                <Chip
                  size="sm"
                  variant="soft"
                  color="danger"
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                      warning
                    </span>
                  }
                >
                  Overdue
                </Chip>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} variant="plain">
            <span className="material-symbols-outlined">close</span>
          </IconButton>
        </Box>

        {/* Task Details */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Description */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 1, color: 'success.700' }}>
                Description
              </Typography>
              <Textarea
                value={editTask.description}
                onChange={e => setEditTask({ ...editTask, description: e.target.value })}
                minRows={2}
                placeholder="Task description..."
                disabled={saving || deleting}
                sx={{ width: '100%' }}
              />
            </Box>

            {/* Due Date */}
            <Box>
              <FlatpickrDateTimeField
                label="Due Date"
                value={editTask.due_date}
                onChange={val => setEditTask({ ...editTask, due_date: val })}
                disabled={saving || deleting}
              />
            </Box>

            {/* Assigned To section removed as requested */}

            {/* Created By */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 1, color: 'success.700' }}>
                Created By
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar size="sm" sx={{ bgcolor: 'neutral.500' }}>
                  {getInitials(task.created_by_user?.display_name)}
                </Avatar>
                <Box>
                  <Typography level="body-sm">
                    {task.created_by_user?.display_name || 'Unknown'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    {formatDate(task.created_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Attachments */}
        {task.task_attachments && task.task_attachments.length > 0 && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography level="title-sm" sx={{ mb: 2, color: 'success.700' }}>
              Attachments ({task.task_attachments.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {task.task_attachments.map((attachment) => (
                <Box
                  key={attachment.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'neutral.200',
                    borderRadius: 'sm',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ color: 'var(--joy-palette-neutral-500)' }}>
                    {attachment.file_type?.startsWith('image/') ? 'image' : 'description'}
                  </span>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography level="body-sm" noWrap>
                      {attachment.file_name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {(attachment.file_size / 1024 / 1024).toFixed(2)} MB • {formatDate(attachment.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        )}

        {/* Comments Section */}
        <Card sx={{ p: 3 }}>
          <Typography level="title-sm" sx={{ mb: 2, color: 'success.700' }}>
            Comments ({task.task_comments?.length || 0})
          </Typography>

          {/* Existing Comments */}
          {task.task_comments && task.task_comments.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {task.task_comments
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                .map((comment) => (
                  <Box key={comment.id} sx={{ display: 'flex', gap: 2 }}>
                    <Avatar size="sm" sx={{ bgcolor: 'success.500', flexShrink: 0 }}>
                      {getInitials(comment.users?.display_name)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography level="title-xs">
                          {comment.users?.display_name || 'Unknown User'}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {formatDate(comment.created_at)}
                        </Typography>
                      </Box>
                      <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                        {comment.comment_text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Box>
          ) : (
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 3 }}>
              No comments yet.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Add Comment */}
          <Box>
            <Typography level="title-xs" sx={{ mb: 1 }}>
              Add a comment
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Textarea
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                minRows={2}
                sx={{ flexGrow: 1 }}
              />
              <EnhancedButton
                onClick={handleAddComment}
                loading={addingComment}
                disabled={!newComment.trim()}
                size="sm"
                variant="solid"
              >
                Post
              </EnhancedButton>
            </Box>
          </Box>
        </Card>

        {/* Actions */}
        {canManageTasks && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <EnhancedButton
              variant="solid"
              color="primary"
              loading={saving}
              disabled={saving || deleting}
              onClick={async () => {
                setSaving(true);
                setError('');
                try {
                  const { error } = await supabase
                    .from('tasks')
                    .update({
                      title: editTask.title,
                      description: editTask.description,
                      priority: Number(editTask.priority),
                      due_date: editTask.due_date ? new Date(editTask.due_date).toISOString() : null,
                    })
                    .eq('id', task.id);
                  if (error) throw error;
                  onTaskUpdated && onTaskUpdated();
                } catch (err) {
                  setError('Error saving task: ' + err.message);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save
            </EnhancedButton>
            <EnhancedButton
              variant="outlined"
              color="success"
              disabled={saving || deleting || task.completed}
              onClick={async () => {
                setSaving(true);
                setError('');
                try {
                  const { error } = await supabase
                    .from('tasks')
                    .update({ completed: true })
                    .eq('id', task.id);
                  if (error) throw error;
                  onTaskUpdated && onTaskUpdated();
                } catch (err) {
                  setError('Error completing task: ' + err.message);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Complete Task
            </EnhancedButton>
            <EnhancedButton
              variant="outlined"
              color="danger"
              loading={deleting}
              disabled={saving || deleting}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Task
            </EnhancedButton>
          </Box>
        )}
        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid #f44336', borderRadius: 2, bgcolor: '#fff0f0' }}>
            <Typography level="body-sm" sx={{ color: 'danger.700', mb: 1 }}>
              Are you sure you want to delete this task? This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <EnhancedButton
                color="danger"
                loading={deleting}
                onClick={async () => {
                  setDeleting(true);
                  setError('');
                  try {
                    const { error } = await supabase
                      .from('tasks')
                      .delete()
                      .eq('id', task.id);
                    if (error) throw error;
                    setShowDeleteConfirm(false);
                    onTaskUpdated && onTaskUpdated();
                    onClose && onClose();
                  } catch (err) {
                    setError('Error deleting task: ' + err.message);
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                Confirm Delete
              </EnhancedButton>
              <EnhancedButton
                variant="outlined"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </EnhancedButton>
            </Box>
          </Box>
        )}
        {error && (
          <Typography level="body-sm" sx={{ color: 'danger.700', mt: 2 }}>{error}</Typography>
        )}
      </Box>
  </Modal>
  );
};

export default TaskDetailPanel;